import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  MessageSquare,
  Map,
  LayoutGrid,
  Bell,
  BookOpen,
  Users,
  Settings,
  MessageCircle,
  Tag,
  Contact,
  ChevronLeft,
  ChevronRight,
  CreditCard
} from "lucide-react";
import { ThemeToggle } from "@/components/theme/ThemeToggle";

const navigationItems = [
  { value: "feedback", label: "Feedback", icon: MessageSquare },
  { value: "roadmap", label: "Roadmap", icon: Map },
  { value: "boards", label: "Boards", icon: LayoutGrid },
  { value: "statuses", label: "Statuses", icon: Tag },
  { value: "changelog", label: "Changelog", icon: Bell },
  { value: "knowledge", label: "Knowledge Base", icon: BookOpen },
  { value: "team", label: "Team", icon: Users },
  { value: "chat", label: "Live Chat", icon: MessageCircle },
  { value: "contacts", label: "Contacts", icon: Contact },
  { value: "billing", label: "Billing", icon: CreditCard },
  { value: "settings", label: "Settings", icon: Settings },
];

type DashboardSidebarProps = {
  userEmail?: string;
  userName?: string;
};

export const DashboardSidebar = ({ userEmail, userName }: DashboardSidebarProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'feedback';
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleNavigate = (value: string) => {
    navigate(`/admin?tab=${value}`);
  };

  return (
    <div
      className={cn(
        "flex flex-col border-r bg-muted/10 transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header with collapse button */}
      <div className="p-4 border-b">
        {!isCollapsed ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Avatar className="h-9 w-9 flex-shrink-0">
                <AvatarFallback>
                  {userName?.[0]?.toUpperCase() || userEmail?.[0]?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">
                  {userName || userEmail?.split("@")[0] || "User"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {userEmail}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback>
                {userName?.[0]?.toUpperCase() || userEmail?.[0]?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Navigation items */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.value;

            return (
              <Button
                key={item.value}
                variant={isActive ? "default" : "ghost"}
                className={cn(
                  "w-full justify-start",
                  isCollapsed ? "px-2" : "px-4",
                  isActive && "bg-primary text-primary-foreground font-medium hover:bg-primary/90"
                )}
                onClick={() => handleNavigate(item.value)}
              >
                <Icon className={cn("h-5 w-5 flex-shrink-0", !isCollapsed && "mr-3")} />
                {!isCollapsed && (
                  <span className="truncate">{item.label}</span>
                )}
              </Button>
            );
          })}
        </div>
      </nav>

      {/* Footer with theme toggle */}
      <div className="border-t p-4">
        <div className={cn("flex", isCollapsed ? "justify-center" : "justify-between items-center")}>
          {!isCollapsed && (
            <span className="text-sm text-muted-foreground">Theme</span>
          )}
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
};
